import { Injectable } from "@angular/core";
import { BiTranslateService } from "@globals/bi-translate";
import L from "leaflet";
import { LantmäterietFastighetsIndelningFeatureProperties } from "../LantmäterietTypes";

@Injectable()
export class LantmäterietMapSetupService {
  private getMvtTilesDataUrl = (tileDataType: number) => `/api/Map/GetSwedishMvtTiles?tileDataType=${tileDataType}&tileMatrix={z}&tileRow={y}&tileCol={x}`;

  private getThickLineStyle(color: string) {
    return { color: color, weight: 2 };
  }

  private getThinLineStyle(color: string) {
    return { color: color, weight: 1 };
  }

  private fastighetsIndelningFeaturesWithThinLines = ["FASTGR", "FAST1:5", "KVTRAKTGR", "TRAKTGR"];

  private byggnadGrid: L.VectorGrid.Protobuf;
  private fastighetsIndelningGrid: L.VectorGrid.Protobuf;
  private currentFeatureColor: "black" | "yellow";

  public constructor(private translator: BiTranslateService) {}

  public setupByggnadVectorGridOverlay(defaultColorForFeatures: string, layerControl: L.Control.Layers) {
    this.byggnadGrid = new L.VectorGrid.Protobuf(this.getMvtTilesDataUrl(2), {
      minZoom: 13,
      maxNativeZoom: 17,
      updateWhenIdle: true,
      vectorTileLayerStyles: {
        anlaggningsomrade: this.getThickLineStyle(defaultColorForFeatures),
        byggnad: this.getThinLineStyle(defaultColorForFeatures)
      },
      getFeatureId: (feature: any) => feature.type
    });
    layerControl.addOverlay(this.byggnadGrid, this.translator.instant("leaflet.BuildingsLayerLabel"));
  }

  public setupFastighetsIndelningVectorGridOverlay(defaultColorForFeatures: string, layerControl: L.Control.Layers) {
    this.fastighetsIndelningGrid = new L.VectorGrid.Protobuf(this.getMvtTilesDataUrl(1), {
      minZoom: 13,
      maxNativeZoom: 17,
      updateWhenIdle: true,
      vectorTileLayerStyles: {
        granspunkter: {
          className: "hidden"
        },
        fiske: {
          className: "hidden"
        },
        text: {
          className: "hidden"
        },
        administrativindelning: {
          className: "hidden"
        },

        fastighetsindelning: (properties: LantmäterietFastighetsIndelningFeatureProperties, _zoom, _geometryDimension) => {
          // Remove parts of the vector tiles that create circle points.
          if (["FASTID", "FASTOID", "SAMFID", "SAMFOID", "SAMF", "FASTIGHET", "3DGR", "3DFASTIGH", "3DFASTID", "3DSAMFID", "3DSAMF"].includes(properties.detaljtyp)) {
            return {
              className: "hidden"
            };
          }

          if (["SOCKNA1:5", "SOCKNAGR", "TRAKT1:5", "FAST1:5TÄT", "FASTTÄTGR", "FASTSTR"].includes(properties.detaljtyp)) return this.getThickLineStyle(defaultColorForFeatures);

          if (this.fastighetsIndelningFeaturesWithThinLines.includes(properties.detaljtyp)) return this.getThinLineStyle(defaultColorForFeatures);
        }
      },
      getFeatureId: (feature: any) => feature.type
    });
    layerControl.addOverlay(this.fastighetsIndelningGrid, this.translator.instant("leaflet.PropertiesLayerLabel"));
  }

  public setupMapBaselayerChangeCallback(map: L.Map, initialFeatureColor: "black" | "yellow") {
    this.currentFeatureColor = initialFeatureColor;

    // When changing layer, we need to change the color of the vector tile layer features for optimal view.
    map.on("baselayerchange", (e: L.LayersControlEvent) => {
      let colorChanged = false;

      const hybridTranslated = this.translator.instant("leaflet.Hybrid");
      // Yellow color on hybrid map, otherwise black
      if (this.currentFeatureColor === "black" && e.name === hybridTranslated) {
        colorChanged = true;
        this.currentFeatureColor = "yellow";
      } else if (this.currentFeatureColor === "yellow" && e.name !== hybridTranslated) {
        colorChanged = true;
        this.currentFeatureColor = "black";
      }

      if (colorChanged) {
        // There are 2 different types of features in the "byggnad" layer: 3 and 1
        this.byggnadGrid.setFeatureStyle(3, this.getThinLineStyle(this.currentFeatureColor));
        this.byggnadGrid.setFeatureStyle(1, this.getThickLineStyle(this.currentFeatureColor));
        this.fastighetsIndelningGrid.setFeatureStyle(2, (props: LantmäterietFastighetsIndelningFeatureProperties) => {
          if (this.fastighetsIndelningFeaturesWithThinLines.includes(props.detaljtyp)) return this.getThinLineStyle(this.currentFeatureColor);
          else return this.getThickLineStyle(this.currentFeatureColor);
        });

        // Leaflet layers control has a bug causing restyled overlays not to be rendered after changing the style.
        // Therefore, we remove and add the layers again
        if (map.hasLayer(this.byggnadGrid)) map.removeLayer(this.byggnadGrid).addLayer(this.byggnadGrid);

        if (map.hasLayer(this.fastighetsIndelningGrid)) map.removeLayer(this.fastighetsIndelningGrid).addLayer(this.fastighetsIndelningGrid);
      }
    });
  }

  public getMvtLayer(layerId: number) {
    return new L.VectorGrid.Protobuf(`https://${window.location.host}/api/Map/ProxyBaseLayer?path=3857/{z}/{y}/{x}.mvt&maplayerid=${layerId}`, {
      minZoom: 13,
      maxNativeZoom: 17,
      updateWhenIdle: true
    });
  }

  public getBaseLayer(layerId: number) {
    return {
      id: 1,
      name: "Lantmäteriet basis",
      layer: new L.TileLayer(`https://${window.location.host}/api/Map/ProxyBaseLayer?path=3857/{z}/{y}/{x}.png&maplayerid=${layerId}`, {
        attribution: "©Lantmäteriet",
        updateWhenIdle: true,
        updateWhenZooming: false,
        updateInterval: 400,
        minZoom: 1,
        maxNativeZoom: 14
      })
    };
  }

  public loadKommunerLayer(): L.GeoJSON {
    const kommunerLayer = L.geoJSON(null, {
      style: {
        color: "blue",
        weight: 1,
        fillOpacity: 0.0
      },
      onEachFeature: (feature, layer) => {
        if (feature.properties && feature.properties.namn) {
          layer.bindPopup(feature.properties.namn);
        }
      }
    });

    const kommunerApiUrl = "https://api.lantmateriet.se/ogc-features/v1/administrativ-indelning/collections/kommuner-2025/items?f=json&limit=10000";

    const headers = new Headers();
    headers.set("Authorization", "Basic " + btoa(`${"blid0002"}:${"8TXRUWadUcDai5512O6h"}`));

    fetch(kommunerApiUrl, { headers })
      .then(response => response.json())
      .then(data => {
        //console.log("Kommunedata fra API:", data); // Log hele responsen ud
        if (data && data.features) {
          kommunerLayer.addData(data);
        } else {
          console.error("Kommunedata er ikke i det forventede format:", data);
        }
      })
      .catch(error => console.error("Fejl ved hentning af kommunedata:", error));

    return kommunerLayer;
  }
}
